import {
  forwardRef,
  useId,
  useState,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label: ReactNode;
  error?: string;
  hint?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, error, hint, id, className, type, ...rest },
  ref,
) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const errId = error ? `${inputId}-err` : undefined;
  const hintId = hint ? `${inputId}-hint` : undefined;
  const describedBy = [errId, hintId].filter(Boolean).join(' ') || undefined;

  // Поля-пароли получают кнопку показа/скрытия введённого пароля (для всех
  // ролей на форме логина и где угодно). type переключается password↔text.
  const isPassword = type === 'password';
  const [revealed, setRevealed] = useState(false);
  const effectiveType = isPassword && revealed ? 'text' : type;

  return (
    <div className={['hsc-input', className].filter(Boolean).join(' ')}>
      <label htmlFor={inputId} className="hsc-input__label">
        {label}
      </label>
      <div className={isPassword ? 'hsc-input__wrap hsc-input__wrap--password' : 'hsc-input__wrap'}>
        <input
          ref={ref}
          id={inputId}
          type={effectiveType}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className="hsc-input__field"
          {...rest}
        />
        {isPassword && (
          <button
            type="button"
            className="hsc-input__reveal"
            onClick={() => setRevealed((v) => !v)}
            aria-label={revealed ? 'Скрыть пароль' : 'Показать пароль'}
            aria-pressed={revealed}
            tabIndex={-1}
            data-testid="password-reveal"
          >
            {revealed ? (
              // глаз перечёркнут (скрыть)
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M3 3l18 18M10.6 10.6a2 2 0 002.8 2.8M9.9 5.1A9.5 9.5 0 0112 5c5 0 9 4.5 9 7a12 12 0 01-2.2 3.2M6.1 6.1A12.4 12.4 0 003 12c0 2.5 4 7 9 7a9.3 9.3 0 003.9-.8"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            ) : (
              // глаз открыт (показать)
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinejoin="round"
                />
                <circle cx="12" cy="12" r="2.6" stroke="currentColor" strokeWidth="1.7" />
              </svg>
            )}
          </button>
        )}
      </div>
      {hint && (
        <span id={hintId} className="hsc-input__hint">
          {hint}
        </span>
      )}
      {error && (
        <span id={errId} role="alert" className="hsc-input__error">
          {error}
        </span>
      )}
    </div>
  );
});
